import type { ApiResponse } from '@creator/sdk/creator-sdk';
import type { LinkMeta } from '@creator/sdk/modules/upvote';
import { post as postApi } from '@creator/sdk/network/api-service';
import creatorSdk from '@src/services/creator-sdk';
import type { Comment } from '@src/store/contentEntities/model';
import { jotaiStore } from '../store';
import { commentCollectionAtomFamily, commentCollectionStateFamily, setComment } from './comments';

export interface CreateAnonymousCommentApiRequestPayload {
    tokenName: string;
    postId: string;
    replyToCommentId: string;
    content: string;
}

export const createAnonymousComment = async (payload: CreateAnonymousCommentApiRequestPayload) => {
    const { tokenName, postId, replyToCommentId } = payload;
    const { data } = await postApi<ApiResponse<Comment>>(
        '/comment/anonymous',
        payload,
        {
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const comment = { ...data.payload, id: data.payload.commentId };
    setComment(comment);
    const { commentId } = comment;
    updateCommentCollection(tokenName, postId, commentId, replyToCommentId);

    return comment;
};

export interface CreateCommentApiRequestPayload {
    publisherId?: string;
    tokenName: string;
    postId: string;
    replyToCommentId: string;
    content: string;
    taggedUsers?: { username: string; id: string }[];
    linkMeta?: LinkMeta | null;
}

export const createComment = async (payload: CreateCommentApiRequestPayload) => {
    const { tokenName, postId, replyToCommentId } = payload;
    const authIdToken = await creatorSdk.accountModule.getIdToken();

    const { data } = await postApi<ApiResponse<Comment>>(
        '/comment/create',
        payload,
        {
            Authorization: `Bearer ${authIdToken}`,
            'X-Firebase-AppCheck': '',
        },
        false,
    );

    const comment = { ...data.payload, id: data.payload.commentId };
    setComment(comment);

    const { commentId } = comment;
    updateCommentCollection(tokenName, postId, commentId, replyToCommentId);
    return comment;
};

function updateCommentCollection(tokenName: string, postId: string, commentId: string, replyToCommentId?: string) {
    const type = replyToCommentId ? 'comment' : 'post';
    // Update the postsAtom with the new comment
    const targetCollectionAtom = commentCollectionAtomFamily({
        type,
        tokenName,
        postId,
        parentCommentId: replyToCommentId,
    });

    // Prepend the new comment ID
    // or Append in case of replyToCommentId
    jotaiStore.set(targetCollectionAtom, draft => {
        if (!draft.ids) draft.ids = [];
        if (draft.ids.includes(commentId)) return;
        if (replyToCommentId) draft.ids.push(commentId);
        if (!replyToCommentId) draft.ids.unshift(commentId);
    });
}
